/*
===============================================================================
LUXWORLD INTERIOR DESIGN STUDIO™
Curtain Track Design Studio
Version: 3.0.0
Last updated: 21 July 2026

PURPOSE
- One data-driven builder for all supported curtain-track collections.
- Track selection is loaded from the URL, for example:
  /pages/curtain-track-design-studio?track=series-74
- Prices and compatibility rules are stored in this file; no third-party
  calculator or collection metafields are required.

LIQUID CONTRACT
The future Liquid section only needs a root element:

<div
  data-lux-curtain-track-studio
  data-currency="AUD"
  data-locale="en-AU"
  data-cart-add-url="/cart/add.js"
  data-cart-url="/cart"
  data-checkout-variant-id="YOUR_VARIANT_ID"
  data-pricing-mode="properties-only"
></div>

Supported pricing modes:
- properties-only: adds one configured product per saved window. The Shopify
  product price must already match your intended checkout arrangement.
- dollar-quantity: quantity equals the rounded total dollar amount. Use only
  when the checkout product variant is priced at $1.00.
- cents-quantity: quantity equals total cents. Use only when the checkout
  product variant is priced at $0.01.

IMPORTANT
This file deliberately does not trust customer-visible price properties as a
secure server-side price. Version 2.1 also avoids full DOM replacement while
customers type, preventing focus loss and screen jumping. For secure commercial checkout, validate pricing on
Shopify/server side before order completion.
===============================================================================
*/

(() => {
  "use strict";

  /* ==========================================================================
     1. VERSION, GLOBAL CONSTANTS AND SHARED OPTIONS
     ========================================================================== */

  const APP_VERSION = "3.3.0";
  const STORAGE_KEY = "luxworld:curtain-track-studio:v3.3.0";
  const INPUT_UPDATE_DELAY_MS = 180;
  const STABLE_INPUT_FIELDS = new Set([
    "lengthMm",
    "customRoomName",
    "customRunnerQuantity",
    "notes",
    "bendDimensions.a",
    "bendDimensions.b",
    "bendDimensions.c"
  ]);

  const LUX_PRICING = Object.freeze({
    WAND_DRAW: 15,
    CORD_DRAW: 70,
    DREAM_CHAIN: 65,
    DREAM_MOTORISED: 599,
    BENDING: 69,
    BAY_WINDOW: 89,
    CORNER_RETURN: 45,
    S_WAVE_HEADING: 40
  });

  const LUX_COLOURS = Object.freeze({
    WHITE: option("white", "White"),
    OFF_WHITE: option("off-white", "Off White"),
    BLACK: option("black", "Black"),
    PLATINUM: option("platinum", "Platinum"),
    CHAMPAGNE: option("champagne", "Champagne")
  });

  const LUX_FIXING = Object.freeze({
    CEILING: option("ceiling", "Ceiling Mounted"),
    WALL: option("wall", "Wall Mounted"),
    RECESS: option("recess", "Recess Mounted")
  });

  const LUX_PLEATING = Object.freeze({
    S_WAVE: pricedOption("s-wave", "S-Wave", LUX_PRICING.S_WAVE_HEADING),
    PINCH_PLEAT: pricedOption("pinch-pleat", "Pinch Pleat", 0)
  });

  const LUX_STACKING = Object.freeze({
    LEFT: option("left-stack", "Left Stack"),
    RIGHT: option("right-stack", "Right Stack"),
    CENTRE: option("centre-open", "Centre Open"),
    FREE: option("free-hanging", "Free Hanging")
  });

  const LUX_BENDING = Object.freeze({
    STRAIGHT: pricedOption("straight", "Straight", 0),
    CURVED: pricedOption("curved", "Bending", LUX_PRICING.BENDING),
    BAY: pricedOption("bay", "Bay Window", LUX_PRICING.BAY_WINDOW)
  });

  const LUX_OPERATIONS = Object.freeze({
    HAND: operation("hand", "Hand Drawn", 0),
    WAND: operation("wand", "Wand Drawn", LUX_PRICING.WAND_DRAW),
    CORD: operation("cord", "Cord Drawn", LUX_PRICING.CORD_DRAW, {
      requiresSideSelection: true,
      sideOptions: [option("left", "Left Side"), option("right", "Right Side")]
    }),
    MOTORISED: operation("motorised", "Motorised", 0)
  });

  const DREAM_SHEER_OPERATIONS = Object.freeze({
    WAND: operation("wand", "Wand Operated", 0),
    CHAIN: operation("chain", "Chain Operated", LUX_PRICING.DREAM_CHAIN, {
      requiresSideSelection: true,
      sideOptions: [option("left", "Left Side"), option("right", "Right Side")]
    }),
    MOTORISED: operation("motorised", "Motorised", LUX_PRICING.DREAM_MOTORISED)
  });

  const MANUAL_DEFAULTS = deepFreeze({
    trackType: "manual",
    length: {
      minimum: 1000,
      maximum: 6000,
      maximumContinuous: 6000,
      priceTierStrategy: "ceiling"
    },
    pleating: [LUX_PLEATING.PINCH_PLEAT, LUX_PLEATING.S_WAVE],
    stacking: [
      LUX_STACKING.LEFT,
      LUX_STACKING.RIGHT,
      LUX_STACKING.CENTRE,
      LUX_STACKING.FREE
    ],
    bending: [LUX_BENDING.STRAIGHT, LUX_BENDING.CURVED, LUX_BENDING.BAY],
    optionalExtras: [],
    manufacturing: {
      supportsSingleTrack: true,
      supportsDoubleTrack: true,
      allowSplitTrack: true,
      allowContinuousTrack: true,
      requiresRunnerSelection: true,
      sharedFixingForDoubleTrack: true,
      sharedColourForDoubleTrack: true,
      bendingChargePerTrack: false
    }
  });

  const MOTORISED_DEFAULTS = deepFreeze({
    trackType: "motorised",
    pleating: [LUX_PLEATING.PINCH_PLEAT, LUX_PLEATING.S_WAVE],
    stacking: [
      LUX_STACKING.LEFT,
      LUX_STACKING.RIGHT,
      LUX_STACKING.CENTRE,
      LUX_STACKING.FREE
    ],
    operations: [LUX_OPERATIONS.MOTORISED],
    fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
    colours: [LUX_COLOURS.OFF_WHITE],
    bending: [LUX_BENDING.STRAIGHT, LUX_BENDING.CURVED, LUX_BENDING.BAY],
    optionalExtras: [],
    manufacturing: {
      supportsSingleTrack: true,
      supportsDoubleTrack: false,
      allowSplitTrack: true,
      allowContinuousTrack: true,
      requiresRunnerSelection: true,
      bendingChargePerTrack: false,
      motorisedTrack: true
    }
  });

  /* ==========================================================================
     2. SHARED PRICE TABLES
     ========================================================================== */

  const PRICE_SERIES_50 = priceTable({
    1000: 90, 1500: 122, 2000: 148, 2500: 173, 3000: 205,
    3500: 237, 4000: 260, 4500: 298, 5000: 330, 5500: 348, 6000: 398
  });

  const PRICE_SERIES_51_57 = priceTable({
    1000: 134, 1500: 172, 2000: 223, 2500: 272, 3000: 293,
    3500: 336, 4000: 372, 4500: 410, 5000: 449, 5500: 482, 6000: 498
  });

  const PRICE_SERIES_56_58 = priceTable({
    1000: 99, 1500: 134, 2000: 165, 2500: 195, 3000: 219,
    3500: 246, 4000: 267, 4500: 297, 5000: 326, 5500: 340, 6000: 371
  });

  const PRICE_SERIES_70 = priceTable({
    1000: 130, 1500: 168, 2000: 210, 2500: 246, 3000: 290,
    3500: 323, 4000: 368, 4500: 412, 5000: 456, 5500: 466, 6000: 504
  });

  const PRICE_SERIES_74 = priceTable({
    1000: 96, 1500: 134, 2000: 164, 2500: 198, 3000: 222,
    3500: 258, 4000: 288, 4500: 318, 5000: 352, 5500: 372, 6000: 408
  });

  const PRICE_SERIES_67 = priceTable({
    1000: 149.43, 1500: 205.40, 2000: 258.83, 2500: 309.45,
    3000: 367.08, 3500: 414.50, 4000: 467.43, 4500: 518.80,
    5000: 566.35, 5500: 607.28, 6000: 658.40
  });

  /* ==========================================================================
     3. TRACK DATABASE
     Latest user-supplied rules override earlier values.
     ========================================================================== */

  const LUX_TRACK_DATABASE = deepFreeze({
    "series-50": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-50",
      aliases: ["series-50-milan", "series-50-milan-curtain-track"],
      collectionHandle: "series-50",
      productHandle: "series-50-milan-curtain-track-modern-slimline-s-fold-track",
      displayName: "Series 50 Milan Curtain Track | Modern Slimline S Fold Track",
      shortName: "Series 50",
      heroTitle: "Series 50 Milan Curtain Track",
      description: "Modern slimline manual curtain track for residential curtain installations.",
      pricing: PRICE_SERIES_50,
      colours: [LUX_COLOURS.WHITE, LUX_COLOURS.BLACK, LUX_COLOURS.PLATINUM],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND]
    }),

    "series-51": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-51",
      aliases: ["series-51-commercial", "series-51-commercial-curtain-track"],
      collectionHandle: "series-51",
      productHandle: "series-51-commercial-curtain-track-heavy-duty-residential-commercial-curtain-tracking",
      displayName: "Series 51 Commercial Curtain Track – Heavy Duty Residential & Commercial Curtain Tracking",
      shortName: "Series 51",
      heroTitle: "Series 51 Commercial Curtain Track",
      description: "Heavy-duty residential and commercial manual curtain track.",
      pricing: PRICE_SERIES_51_57,
      colours: [LUX_COLOURS.WHITE, LUX_COLOURS.BLACK],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND],
      manufacturing: { heavyDutyTrack: true }
    }),

    "series-56": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-56",
      aliases: ["series-56-curtain-track"],
      collectionHandle: "series-56",
      productHandle: "series-56-curtain-track-easy-clip-s-fold-curtain-tracking-perth",
      displayName: "Series 56 Curtain Track | Easy Clip S Fold Curtain Tracking Perth",
      shortName: "Series 56",
      heroTitle: "Series 56 Easy Clip Curtain Track",
      description: "Easy Clip manual curtain track for S-Wave and pinch pleat curtains.",
      pricing: PRICE_SERIES_56_58,
      colours: [LUX_COLOURS.WHITE, LUX_COLOURS.BLACK],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND]
    }),

    "series-57": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-57",
      aliases: ["series-57-fineline", "series-57-fineline-curtain-track"],
      collectionHandle: "series-57",
      productHandle: "series-57-fineline-curtain-track-commercial-s-fold-curtain-tracking",
      displayName: "Series 57 Fineline Curtain Track | Commercial S Fold Curtain Tracking",
      shortName: "Series 57",
      heroTitle: "Series 57 Fineline Curtain Track",
      description: "Commercial Fineline manual curtain track for S-Wave and pinch pleat curtains.",
      pricing: PRICE_SERIES_51_57,
      colours: [LUX_COLOURS.WHITE, LUX_COLOURS.BLACK, LUX_COLOURS.PLATINUM],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND],
      manufacturing: { heavyDutyTrack: true }
    }),

    "series-58": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-58",
      aliases: ["series-58-slimline", "series-58-slimline-curtain-track"],
      collectionHandle: "series-58",
      productHandle: "series-58-slimline-curtain-track",
      displayName: "Series 58 Slimline Curtain Track",
      shortName: "Series 58",
      heroTitle: "Series 58 Slimline Curtain Track",
      description: "Slimline manual curtain track for residential and commercial curtain installations.",
      pricing: PRICE_SERIES_56_58,
      colours: [LUX_COLOURS.WHITE, LUX_COLOURS.BLACK],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND]
    }),

    "series-67": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-67",
      aliases: ["series-67-curtain-track", "series-67-heavy-duty-commercial-tracks"],
      collectionHandle: "series-67",
      productHandle: "series-67-curtain-track-heavy-duty-commercial-tracks",
      displayName: "Series 67 Curtain Track | Heavy Duty Commercial Tracks",
      shortName: "Series 67",
      heroTitle: "Series 67 Heavy Duty Commercial Curtain Track",
      description: "Heavy-duty commercial manual track with cord, wand and hand-drawn operation.",
      pricing: PRICE_SERIES_67,
      length: {
        minimum: 1000,
        maximum: 6000,
        maximumContinuous: 6000,
        priceTierStrategy: "ceiling"
      },
      colours: [
        LUX_COLOURS.WHITE,
        LUX_COLOURS.BLACK,
        LUX_COLOURS.PLATINUM,
        LUX_COLOURS.CHAMPAGNE
      ],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND, LUX_OPERATIONS.CORD],
      optionalExtras: [
        pricedOption("corner-return", "Corner Return", LUX_PRICING.CORNER_RETURN)
      ],
      manufacturing: { heavyDutyTrack: true }
    }),

    "series-70": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-70",
      aliases: ["series-70-recess", "series-70-recess-curtain-track"],
      collectionHandle: "series-70",
      productHandle: "series-70-recess-curtain-track-hidden-ceiling-track-system-for-luxury-interiors",
      displayName: "Series 70 Recess Curtain Track - Hidden Ceiling Track System For Luxury Interiors",
      shortName: "Series 70",
      heroTitle: "Series 70 Recess Curtain Track",
      description: "Concealed recessed manual curtain track for luxury interiors.",
      pricing: PRICE_SERIES_70,
      colours: [LUX_COLOURS.WHITE],
      fixing: [LUX_FIXING.RECESS],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND],
      manufacturing: {
        allowSplitTrack: false,
        recessedTrack: true
      }
    }),

    "series-74": mergeTrack(MANUAL_DEFAULTS, {
      id: "series-74",
      aliases: ["series-74-venice", "series-74-venice-curtain-track"],
      collectionHandle: "series-74",
      productHandle: "series-74-venice-curtain-track-s-fold-bendable-curtain-track-perth",
      displayName: "Series 74 Venice Curtain Track | S Fold & Bendable Curtain Track Perth",
      shortName: "Series 74",
      heroTitle: "Series 74 Venice S-Fold & Bendable Curtain Track",
      description: "Premium bendable manual curtain track with cord, wand and hand operation.",
      pricing: PRICE_SERIES_74,
      colours: [
        LUX_COLOURS.WHITE,
        LUX_COLOURS.BLACK,
        LUX_COLOURS.PLATINUM,
        LUX_COLOURS.CHAMPAGNE
      ],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND, LUX_OPERATIONS.WAND, LUX_OPERATIONS.CORD],
      optionalExtras: [
        pricedOption("corner-return", "Corner Return", LUX_PRICING.CORNER_RETURN)
      ]
    }),

    "glidemaster-flexi-curtain-tracks": mergeTrack(MANUAL_DEFAULTS, {
      id: "glidemaster-flexi-curtain-tracks",
      aliases: ["glidemaster-flexi", "glidemaster-flexi-curtain-track"],
      collectionHandle: "glidemaster-flexi-curtain-tracks",
      productHandle: "glidemaster-flexi-curtain-tracks",
      displayName: "GlideMaster Flexi Curtain Tracks / Onsite Curve Tracks",
      shortName: "GlideMaster Flexi",
      heroTitle: "GlideMaster Flexi Onsite Curve Track",
      description: "Flexible manual curtain track designed for onsite shaping.",
      pricing: PRICE_SERIES_74,
      colours: [LUX_COLOURS.OFF_WHITE],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [LUX_OPERATIONS.HAND],
      bending: [
        pricedOption("straight", "Straight", 0),
        pricedOption("curved", "Bending", 0),
        pricedOption("bay", "Bay Window", 0)
      ],
      manufacturing: {
        flexibleTrack: true,
        onsiteBending: true,
        factoryBendingRequired: false,
        bendingChargePerTrack: false
      }
    }),

    "dream-sheer-blinds-tracks": mergeTrack(MANUAL_DEFAULTS, {
      id: "dream-sheer-blinds-tracks",
      aliases: ["dream-sheer-blinds-track", "dream-sheer-track"],
      collectionHandle: "collections-curtain-tracks-perth",
      productHandle: "dream-sheer-blinds-tracks",
      displayName: "Dream Sheer Blinds Tracks",
      shortName: "Dream Sheer Blinds Track",
      heroTitle: "Dream Sheer Blinds Track",
      description: "Dedicated operating track for Dream Sheer blinds.",
      pricing: PRICE_SERIES_74,
      pleating: [],
      stacking: [LUX_STACKING.LEFT, LUX_STACKING.RIGHT, LUX_STACKING.CENTRE],
      colours: [LUX_COLOURS.WHITE],
      fixing: [LUX_FIXING.CEILING, LUX_FIXING.WALL],
      operations: [DREAM_SHEER_OPERATIONS.WAND, DREAM_SHEER_OPERATIONS.CHAIN, DREAM_SHEER_OPERATIONS.MOTORISED],
      bending: [],
      manufacturing: {
        supportsSingleTrack: true,
        supportsDoubleTrack: false,
        allowSplitTrack: true,
        allowContinuousTrack: true,
        requiresRunnerSelection: false,
        sharedFixingForDoubleTrack: false,
        sharedColourForDoubleTrack: false,
        bendingChargePerTrack: false,
        hideTrackLayout: true,
        hideHeading: true,
        hideHardware: true,
        hideTrackShape: true,
        dreamSheerTrack: true
      }
    }),

    "lux-glide-smart-led-motorised-curtain-tracks": mergeTrack(MOTORISED_DEFAULTS, {
      id: "lux-glide-smart-led-motorised-curtain-tracks",
      aliases: ["lux-glide-smart-led", "glidemaster-led-motorised-curtain-tracks"],
      collectionHandle: "lux-glide-smart-led-motorised-curtain-tracks",
      productHandle: "lux-glide-smart-led-motorised-curtain-tracks",
      displayName: "GlideMaster™ LED Motorised Curtain Tracks Powered by NOVO",
      shortName: "LUX Glide Smart LED",
      heroTitle: "GlideMaster™ LED Motorised Curtain Track",
      description: "Smart motorised curtain track with integrated LED lighting powered by NOVO.",
      length: {
        minimum: 1000,
        maximum: 6500,
        maximumContinuous: 6500,
        firstPricingTier: 2000,
        priceTierStrategy: "ceiling"
      },
      pricing: priceTable({
        2000: 798, 3000: 875, 4000: 945,
        5000: 989, 6000: 1050, 6500: 1080
      }),
      motorisation: {
        enabled: true,
        integratedLed: true,
        motorBrand: "NOVO",
        motorIncludedInBasePrice: true,
        ledIncludedInBasePrice: true
      },
      manufacturing: {
        integratedLedTrack: true
      }
    }),

    "somfy-glydea-ultra-60e": mergeTrack(MOTORISED_DEFAULTS, {
      id: "somfy-glydea-ultra-60e",
      aliases: [
        "smofy-glydea-ultra-curtain-tracks-60e-s-wave",
        "somfy-glydea-ultra-curtain-tracks-60e-s-wave"
      ],
      collectionHandle: "somfy-glydea-ultra-60e",
      productHandle: "somfy-glydea-ultra-60e",
      displayName: "Somfy Glydea™ ULTRA Curtain Tracks 60e S Wave Powered by Somfy Motor",
      shortName: "Somfy Glydea ULTRA 60e",
      heroTitle: "Somfy Glydea™ ULTRA 60e Motorised Curtain Track",
      description: "Premium motorised curtain track powered by the Somfy Glydea ULTRA 60e motor.",
      length: {
        minimum: 1000,
        maximum: 12000,
        maximumContinuous: 12000,
        firstPricingTier: 2000,
        priceTierStrategy: "ceiling"
      },
      pricing: priceTable({
        2000: 1820, 3000: 1948, 4000: 2111, 5000: 2319,
        6000: 2523, 7000: 2725, 8000: 2910, 9000: 3114,
        10000: 3307, 11000: 3502, 12000: 3714
      }),
      motorisation: {
        enabled: true,
        motorBrand: "Somfy",
        motorModel: "Glydea ULTRA 60e",
        motorIncludedInBasePrice: true,
        integratedLed: false
      },
      manufacturing: {
        somfySystem: true,
        premiumTrack: true
      }
    }),

    "series-82-powered-by-somfy": mergeTrack(MOTORISED_DEFAULTS, {
      id: "series-82-powered-by-somfy",
      aliases: ["series-82", "series-82-somfy"],
      collectionHandle: "series-82-powered-by-somfy",
      productHandle: "series-82-powered-by-somfy",
      displayName: "Series 82 Powered by Somfy",
      shortName: "Series 82",
      heroTitle: "Series 82 Motorised Curtain Track Powered by Somfy",
      description: "Premium motorised curtain track powered by Somfy.",
      length: {
        minimum: 1000,
        maximum: 6500,
        maximumContinuous: 6500,
        firstPricingTier: 2000,
        priceTierStrategy: "ceiling"
      },
      pricing: priceTable({
        2000: 1790, 3000: 1907, 4000: 2018,
        5000: 2160, 6000: 2290, 6500: 2427
      }),
      motorisation: {
        enabled: true,
        motorBrand: "Somfy",
        motorIncludedInBasePrice: true,
        integratedLed: false
      },
      manufacturing: {
        somfySystem: true
      }
    })
  });

  /* ==========================================================================
     4. HARDWARE AND S-WAVE CARRIER TABLES
     ========================================================================== */

  const LUX_BRACKET_TABLE = deepFreeze([
    hardwareRow(1000, 3), hardwareRow(1500, 4), hardwareRow(2000, 5),
    hardwareRow(2500, 6), hardwareRow(3000, 6), hardwareRow(3500, 7),
    hardwareRow(4000, 7), hardwareRow(4500, 8), hardwareRow(5000, 8),
    hardwareRow(5500, 9), hardwareRow(6000, 9)
  ]);

  const LUX_SWAVE_ONE_WAY_CARRIERS = deepFreeze([
    carrierRow(308, 6), carrierRow(426, 8), carrierRow(544, 10),
    carrierRow(662, 12), carrierRow(780, 14), carrierRow(893, 16),
    carrierRow(1011, 18), carrierRow(1129, 20), carrierRow(1247, 22),
    carrierRow(1370, 24), carrierRow(1488, 26), carrierRow(1611, 28),
    carrierRow(1734, 30), carrierRow(1852, 32), carrierRow(1970, 34),
    carrierRow(2093, 36), carrierRow(2211, 38), carrierRow(2329, 40),
    carrierRow(2447, 42), carrierRow(2570, 44), carrierRow(2688, 46),
    carrierRow(2806, 48), carrierRow(2929, 50), carrierRow(3047, 52),
    carrierRow(3165, 54), carrierRow(3288, 56), carrierRow(3406, 58),
    carrierRow(3524, 60), carrierRow(3642, 62), carrierRow(3760, 64),
    carrierRow(3883, 66), carrierRow(4001, 68), carrierRow(4119, 70),
    carrierRow(4237, 72), carrierRow(4360, 74), carrierRow(4478, 76),
    carrierRow(4596, 78), carrierRow(4714, 80), carrierRow(4835, 82),
    carrierRow(4953, 84), carrierRow(5071, 86), carrierRow(5194, 88),
    carrierRow(5312, 90), carrierRow(5430, 92), carrierRow(5553, 94),
    carrierRow(5671, 96), carrierRow(5789, 98), carrierRow(5902, 100),
    carrierRow(6020, 102), carrierRow(6143, 104), carrierRow(6261, 106),
    carrierRow(6379, 108), carrierRow(6497, 110), carrierRow(6615, 112),
    carrierRow(6733, 114)
  ]);

  const LUX_SWAVE_CENTRE_OPEN_CARRIERS = deepFreeze([
    centreCarrierRow(616, 6), centreCarrierRow(852, 8), centreCarrierRow(1088, 10),
    centreCarrierRow(1324, 12), centreCarrierRow(1560, 14), centreCarrierRow(1786, 16),
    centreCarrierRow(2022, 18), centreCarrierRow(2258, 20), centreCarrierRow(2494, 22),
    centreCarrierRow(2740, 24), centreCarrierRow(2976, 26), centreCarrierRow(3222, 28),
    centreCarrierRow(3468, 30), centreCarrierRow(3704, 32), centreCarrierRow(3940, 34),
    centreCarrierRow(4186, 36), centreCarrierRow(4422, 38), centreCarrierRow(4658, 40),
    centreCarrierRow(4894, 42), centreCarrierRow(5140, 44), centreCarrierRow(5376, 46),
    centreCarrierRow(5612, 48), centreCarrierRow(5858, 50), centreCarrierRow(6094, 52),
    centreCarrierRow(6330, 54), centreCarrierRow(6576, 56), centreCarrierRow(6812, 58),
    centreCarrierRow(7048, 60)
  ]);

  /* ==========================================================================
     5. STATE MODEL
     ========================================================================== */

  function createInitialState(track) {
    const firstOperation = track.operations[0]?.value || "hand";
    const firstFixing = track.fixing[0]?.value || "";
    const firstColour = track.colours[0]?.value || "";
    const firstPleating = track.pleating[0]?.value || "pinch-pleat";
    const firstBending = track.bending[0]?.value || "straight";

    return {
      version: APP_VERSION,
      activeTrackId: track.id,
      activeSection: "track-layout",
      completedSections: [],
      editingWindowId: null,
      draft: {
        id: createId("window"),
        roomName: "Living Room",
        customRoomName: "",
        trackCount: 1,
        lengthMm: track.length.minimum,
        fixing: firstFixing,
        colour: firstColour,
        bending: firstBending,
        bendDimensions: { a: "", b: "", c: "" },
        optionalExtras: [],
        notes: "",
        tracks: [
          createTrackLine(1, firstOperation, firstPleating),
          createTrackLine(2, firstOperation, firstPleating)
        ]
      },
      windows: [],
      ui: {
        busy: false,
        message: "",
        error: "",
        summaryOpen: false,
        reviewOpen: false,
        windowSaved: false,
        projectReview: false,
        projectSaved: false
      }
    };
  }

  function createTrackLine(index, operationValue, pleatingValue) {
    return {
      key: index === 1 ? "front" : "back",
      label: index === 1 ? "Front Track" : "Back Track",
      operation: operationValue,
      cordSide: "left",
      pleating: pleatingValue,
      stacking: "centre-open",
      runnerMode: "standard",
      customRunnerQuantity: ""
    };
  }

  /* ==========================================================================
     6. PRICE, HARDWARE AND MANUFACTURING ENGINES
     ========================================================================== */

  function getPriceTier(track, lengthMm) {
    const width = toPositiveNumber(lengthMm);
    if (!width) return null;

    const tiers = Object.keys(track.pricing)
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    if (!tiers.length) return null;
    return tiers.find((tier) => width <= tier) ?? null;
  }

  function getBaseTrackPrice(track, lengthMm) {
    const tier = getPriceTier(track, lengthMm);
    if (!tier) return { tier: null, price: 0 };
    return { tier, price: Number(track.pricing[tier]) || 0 };
  }

  function calculateTrackPrice(track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    const base = getBaseTrackPrice(track, draft.lengthMm);
    const lines = draft.tracks.slice(0, count);

    const operationTotal = sum(lines.map((line) => {
      return findByValue(track.operations, line.operation)?.price || 0;
    }));

    const pleatingTotal = sum(lines.map((line) => {
      return findByValue(track.pleating, line.pleating)?.price || 0;
    }));

    const bend = findByValue(track.bending, draft.bending);
    const bendingMultiplier = track.manufacturing.bendingChargePerTrack ? count : 1;
    const bendingTotal = (bend?.price || 0) * bendingMultiplier;

    const extrasTotal = sum((draft.optionalExtras || []).map((value) => {
      return findByValue(track.optionalExtras, value)?.price || 0;
    }));

    const baseTotal = base.price * count;
    const total = roundCurrency(baseTotal + operationTotal + pleatingTotal + bendingTotal + extrasTotal);

    return {
      currency: "AUD",
      priceTierMm: base.tier,
      unitBasePrice: base.price,
      trackCount: count,
      baseTotal,
      operationTotal,
      pleatingTotal,
      bendingTotal,
      extrasTotal,
      total
    };
  }

  function getBracketCount(lengthMm) {
    const width = toPositiveNumber(lengthMm);
    if (!width) return 0;
    const match = LUX_BRACKET_TABLE.find((row) => width <= row.maxLengthMm);
    return match?.brackets || LUX_BRACKET_TABLE.at(-1)?.brackets || 0;
  }

  function getSWaveCarrierAllocation(lengthMm, stacking) {
    const width = toPositiveNumber(lengthMm);
    if (!width) return emptyCarrierAllocation(stacking);

    if (stacking === "centre-open") {
      const match = LUX_SWAVE_CENTRE_OPEN_CARRIERS.find(
        (row) => width <= row.maxLengthMm
      );
      if (!match) return emptyCarrierAllocation(stacking);
      return {
        mode: "centre-open",
        carriersPerSide: match.carriersPerSide,
        totalCarriers: match.carriersPerSide * 2
      };
    }

    const match = LUX_SWAVE_ONE_WAY_CARRIERS.find(
      (row) => width <= row.maxLengthMm
    );
    return {
      mode: "one-way",
      carriersPerSide: null,
      totalCarriers: match?.carriers || 0
    };
  }

  function getRunnerAllocation(line, lengthMm) {
    if (line.runnerMode === "custom") {
      const custom = Math.max(0, Math.floor(Number(line.customRunnerQuantity) || 0));
      return {
        mode: "custom",
        carriersPerSide: line.stacking === "centre-open" ? custom / 2 : null,
        totalCarriers: custom
      };
    }

    if (line.pleating === "s-wave") {
      return getSWaveCarrierAllocation(lengthMm, line.stacking);
    }

    // The user supplied the bracket/runner guide as the standard included
    // hardware table for pinch pleat. This table uses the same 500 mm tiers.
    const tier = getCeilingHardwareTier(lengthMm);
    const standard = {
      1000: 14, 1500: 20, 2000: 26, 2500: 32, 3000: 38,
      3500: 44, 4000: 50, 4500: 56, 5000: 62, 5500: 68, 6000: 74
    };
    return {
      mode: "standard",
      carriersPerSide: line.stacking === "centre-open" ? (standard[tier] || 0) / 2 : null,
      totalCarriers: standard[tier] || 0
    };
  }

  function buildManufacturingData(track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    const pricing = calculateTrackPrice(track, draft);
    const tracks = draft.tracks.slice(0, count).map((line, index) => {
      const operationConfig = findByValue(track.operations, line.operation);
      const runner = getRunnerAllocation(line, draft.lengthMm);
      const output = {
        position: index === 0 ? "Front" : "Back",
        operation: operationConfig?.label || line.operation,
        pleating: track.manufacturing.hideHeading ? null : labelFor(track.pleating, line.pleating),
        stacking: labelFor(track.stacking, line.stacking)
      };

      if (!track.manufacturing.hideHardware) {
        output.runnerMode = line.runnerMode === "custom" ? "Custom Runner Quantity" : "Luxworld Standard Runner Count";
        output.runnersTotal = runner.totalCarriers;
      }

      if (operationConfig?.requiresSideSelection) {
        output.controlSide = line.cordSide === "right" ? "Right Side" : "Left Side";
      }
      if (!track.manufacturing.hideHardware && line.stacking === "centre-open" && runner.carriersPerSide !== null) {
        output.runnersPerSide = runner.carriersPerSide;
      }
      return output;
    });

    const result = {
      configurationId: draft.id,
      roomName: resolvedRoomName(draft),
      collection: track.displayName,
      trackCode: track.id,
      trackType: titleCase(track.trackType),
      trackCount: count === 2 ? "Double Track" : "Single Track",
      enteredLengthMm: Number(draft.lengthMm),
      chargedLengthTierMm: pricing.priceTierMm,
      fixing: labelFor(track.fixing, draft.fixing),
      colour: labelFor(track.colours, draft.colour),
      bending: track.bending.length ? labelFor(track.bending, draft.bending) : null,
      tracks,
      price: pricing
    };

    if (!track.manufacturing.hideHardware) {
      result.bracketType = count === 2 ? "Double Track Bracket" : "Single Track Bracket";
      result.bracketQuantity = getBracketCount(draft.lengthMm);
    }

    if (track.bending.length && draft.bending === "bay") {
      result.bayDimensionsMm = cleanDimensions(draft.bendDimensions, ["a", "b", "c"]);
    } else if (draft.bending === "curved") {
      result.curveDimensionsMm = cleanDimensions(draft.bendDimensions, ["a", "b"]);
    }

    const extras = (draft.optionalExtras || [])
      .map((value) => findByValue(track.optionalExtras, value))
      .filter(Boolean)
      .map((item) => item.label);
    if (extras.length) result.optionalExtras = extras;
    if (String(draft.notes || "").trim()) result.notes = String(draft.notes).trim();

    return result;
  }

  /* ==========================================================================
     7. VALIDATION ENGINE
     ========================================================================== */

  function validateDraft(track, draft) {
    const errors = [];
    const length = Number(draft.lengthMm);

    if (!resolvedRoomName(draft)) errors.push("Please enter or select a room name.");
    if (!Number.isFinite(length)) errors.push("Please enter a valid track length.");
    if (Number.isFinite(length) && length < track.length.minimum) {
      errors.push(`Minimum track length is ${track.length.minimum} mm.`);
    }
    if (Number.isFinite(length) && length > track.length.maximum) {
      if (track.length.declaredMaximum && length <= track.length.declaredMaximum) {
        errors.push(
          `${track.shortName} has been declared up to ${track.length.declaredMaximum} mm, ` +
          `but the supplied price table currently ends at ${track.length.maximum} mm.`
        );
      } else {
        errors.push(`Maximum priced track length is ${track.length.maximum} mm.`);
      }
    }
    if (!getPriceTier(track, length)) {
      errors.push("No price tier is available for this length.");
    }

    if (!findByValue(track.fixing, draft.fixing)) errors.push("Please choose a valid fixing method.");
    if (!findByValue(track.colours, draft.colour)) errors.push("Please choose a valid track colour.");
    if (track.bending.length && !findByValue(track.bending, draft.bending)) errors.push("Please choose a valid track shape.");

    const lineCount = draft.trackCount === 2 ? 2 : 1;
    draft.tracks.slice(0, lineCount).forEach((line, index) => {
      const name = index === 0 ? "Front track" : "Back track";
      const operationConfig = findByValue(track.operations, line.operation);
      if (!operationConfig) errors.push(`${name}: choose a valid operating style.`);
      if (operationConfig?.requiresSideSelection && !["left", "right"].includes(line.cordSide)) {
        errors.push(`${name}: choose the cord control side.`);
      }
      if (!track.manufacturing.hideHeading && !findByValue(track.pleating, line.pleating)) errors.push(`${name}: choose a valid pleating style.`);
      if (!findByValue(track.stacking, line.stacking)) errors.push(`${name}: choose a valid stacking style.`);
      if (track.manufacturing.requiresRunnerSelection && line.runnerMode === "custom" && !(Number(line.customRunnerQuantity) > 0)) {
        errors.push(`${name}: enter a valid custom runner quantity.`);
      }
    });

    if (draft.bending === "bay") {
      const a = Number(draft.bendDimensions.a);
      const b = Number(draft.bendDimensions.b);
      const c = Number(draft.bendDimensions.c);
      if (![a, b, c].every((value) => value > 0)) {
        errors.push("Bay window: enter A, B and C measurements.");
      } else if (Math.abs((a + b + c) - length) > 5) {
        errors.push("Bay window: A + B + C must equal the total track length (±5 mm).");
      }
    }

    if (track.bending.length && draft.bending === "curved") {
      const a = Number(draft.bendDimensions.a);
      const b = Number(draft.bendDimensions.b);
      if (![a, b].every((value) => value > 0)) {
        errors.push("Curved track: enter the required curve measurements A and B.");
      }
    }

    return { valid: errors.length === 0, errors };
  }


  /* ==========================================================================
     8. TRACK STUDIO 3.0 — ONE-PAGE ACCORDION APPLICATION
     ========================================================================== */

  const ACCORDION_SECTIONS = Object.freeze([
    sectionDef("track-layout", "Track Layout", "Choose a single or double track system."),
    sectionDef("room-width", "Room and Exact Width", "Name the room and enter the exact finished width."),
    sectionDef("operation", "Operation", "Choose how each track will operate."),
    sectionDef("heading-stacking", "Heading and Stacking", "Choose curtain heading and opening direction."),
    sectionDef("fixing-colour", "Fixing and Colour", "Choose installation method and finish."),
    sectionDef("track-shape", "Track Shape", "Choose straight, bending or bay."),
    sectionDef("extras", "Optional Extras", "Add compatible extras."),
    sectionDef("included-hardware", "Included Hardware", "Review brackets, runners and included hardware."),
    sectionDef("customer-notes", "Customer Notes", "Add important manufacturing or installation notes."),
    sectionDef("review-save", "Review and Save", "Review and save this window.")
  ]);

  class LuxCurtainTrackStudio {
    constructor(root) {
      this.root = root;
      this.settings = readSettings(root);
      this.track = resolveTrackFromLocation();
      this.productImage = resolveProductImageFromLocation(root);
      this.state = this.track ? createInitialState(this.track) : null;
      this.inputSaveTimer = null;
      this.dynamicUpdateTimer = null;
      this.currency = new Intl.NumberFormat(this.settings.locale, {
        style: "currency",
        currency: this.settings.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      if (this.track) {
        this.restore();
        this.normaliseState();
      }
      this.bind();
      this.render();
      this.loadTrackHeroImage();
    }

    async loadTrackHeroImage() {
      if (!this.track) return;

      const image = await resolveTrackHeroImage(this.track, this.root, this.productImage);
      if (!image || image === this.productImage) return;

      this.productImage = image;
      this.updateDynamicPanels();

      this.root.querySelectorAll("[data-track-hero-image]").forEach((img) => {
        img.src = image;
        img.hidden = false;
        if (img.parentElement) img.parentElement.hidden = false;
      });
    }

    normaliseState() {
      if (!this.state.activeSection) this.state.activeSection = "track-layout";
      if (!Array.isArray(this.state.completedSections)) this.state.completedSections = [];
      if (!this.state.ui) this.state.ui = {};
      if (typeof this.state.ui.summaryOpen !== "boolean") this.state.ui.summaryOpen = false;
      if (typeof this.state.ui.windowSaved !== "boolean") this.state.ui.windowSaved = false;
      if (typeof this.state.ui.projectReview !== "boolean") this.state.ui.projectReview = false;
      if (typeof this.state.ui.projectSaved !== "boolean") this.state.ui.projectSaved = false;
    }

    bind() {
      this.root.addEventListener("click", (e) => this.onClick(e));
      this.root.addEventListener("input", (e) => this.onInput(e));
      this.root.addEventListener("change", (e) => this.onChange(e));
    }

    onClick(event) {
      const node = event.target.closest("[data-action]");
      if (!node || !this.root.contains(node)) return;
      event.preventDefault();
      const action = node.dataset.action;
      const actions = {
        "open-section": () => this.openSection(node.dataset.section),
        "select-option": () => this.selectOption(node),
        "toggle-extra": () => this.toggleExtra(node.dataset.value),
        "complete-section": () => this.completeSection(node.dataset.section),
        "save-window": () => this.saveWindow(),
        "edit-window": () => this.editWindow(node.dataset.id),
        "duplicate-window": () => this.duplicateWindow(node.dataset.id),
        "remove-window": () => this.removeWindow(node.dataset.id),
        "new-window": () => this.newWindow(),
        "review-project": () => this.reviewProject(),
        "back-to-builder": () => this.backToBuilder(),
        "save-project": () => this.saveProject(),
        "clear-project": () => this.clearProject(),
        "checkout": () => this.checkout(),
        "open-summary": () => this.setUI({ summaryOpen: true }),
        "close-summary": () => this.setUI({ summaryOpen: false }),
        "close-message": () => this.setUI({ message: "", error: "" })
      };
      actions[action]?.();
    }

    onInput(event) {
      const field = event.target.closest("[data-field]");
      if (!field || !this.state) return;
      this.setField(field.dataset.field, field.value, field.dataset.trackIndex, { render: false, save: false });
      clearTimeout(this.inputSaveTimer);
      this.inputSaveTimer = setTimeout(() => this.save(false), INPUT_UPDATE_DELAY_MS);
      this.queueDynamicUpdate();
    }

    onChange(event) {
      const field = event.target.closest("[data-field]");
      if (!field || !this.state) return;
      const value = field.type === "checkbox" ? field.checked : field.value;
      const stable = STABLE_INPUT_FIELDS.has(field.dataset.field);
      this.setField(field.dataset.field, value, field.dataset.trackIndex, {
        render: !stable,
        save: true
      });
      if (stable) this.updateDynamicPanels();
    }

    setField(path, value, trackIndex, options = {}) {
      const { render = true, save = true } = options;
      const target = trackIndex !== undefined
        ? this.state.draft.tracks[Number(trackIndex)]
        : this.state.draft;
      setByPath(target, path, normaliseFieldValue(path, value));
      this.state.ui.error = "";
      if (save) this.save(false);
      if (render) this.render();
    }

    queueDynamicUpdate() {
      clearTimeout(this.dynamicUpdateTimer);
      this.dynamicUpdateTimer = setTimeout(() => this.updateDynamicPanels(), INPUT_UPDATE_DELAY_MS);
    }

    updateDynamicPanels() {
      if (!this.track || !this.state) return;
      const pricing = calculateTrackPrice(this.track, this.state.draft);
      this.root.querySelectorAll("[data-live-summary]").forEach((el) => {
        el.innerHTML = renderLiveSummaryContent(this.track, this.state.draft, pricing, this.currency, this.productImage);
      });
      this.root.querySelectorAll("[data-mobile-total]").forEach((el) => {
        el.textContent = this.currency.format(pricing.total);
      });
    }

    openSection(id) {
      if (!visibleSections(this.track).some((s) => s.id === id)) return;
      this.state.activeSection = id;
      this.state.ui.error = "";
      this.save(false);
      this.render();
    }

    completeSection(id) {
      const validation = validateAccordionSection(id, this.track, this.state.draft);
      if (!validation.valid) {
        this.setUI({ error: validation.errors.join(" ") });
        return;
      }
      if (!this.state.completedSections.includes(id)) this.state.completedSections.push(id);
      const sections = visibleSections(this.track);
      const index = sections.findIndex((s) => s.id === id);
      this.state.activeSection = sections[Math.min(index + 1, sections.length - 1)].id;
      this.save(false);
      this.render();
      requestAnimationFrame(() => {
        this.root.querySelector(`[data-accordion-section="${cssEscape(this.state.activeSection)}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    selectOption(node) {
      this.setField(node.dataset.field, node.dataset.value, node.dataset.trackIndex, { render: false, save: true });
      this.render();
    }

    toggleExtra(value) {
      const extras = new Set(this.state.draft.optionalExtras || []);
      extras.has(value) ? extras.delete(value) : extras.add(value);
      this.state.draft.optionalExtras = [...extras];
      this.save(false);
      this.render();
    }

    saveWindow() {
      const validation = validateDraft(this.track, this.state.draft);
      if (!validation.valid) {
        this.setUI({ error: validation.errors.join(" ") });
        return;
      }

      const clean = clone(this.state.draft);
      const index = this.state.windows.findIndex((item) => item.id === clean.id);
      if (index >= 0) this.state.windows[index] = clean;
      else this.state.windows.push(clean);

      this.state.editingWindowId = clean.id;
      this.state.ui.windowSaved = true;
      this.state.ui.projectReview = false;
      this.state.ui.projectSaved = false;
      this.state.ui.error = "";
      this.state.ui.message = "";
      this.save(false);
      this.render();

      requestAnimationFrame(() => {
        this.root.querySelector(".lux-ct-after-save")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    editWindow(id) {
      const item = this.state.windows.find((x) => x.id === id);
      if (!item) return;
      this.state.draft = clone(item);
      this.state.editingWindowId = id;
      this.state.activeSection = "track-layout";
      this.state.completedSections = [];
      this.render();
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    duplicateWindow(id) {
      const item = this.state.windows.find((x) => x.id === id);
      if (!item) return;
      const copy = clone(item);
      copy.id = createId("window");
      copy.roomName = "Custom";
      copy.customRoomName = `${resolvedRoomName(item)} Copy`;
      this.state.windows.push(copy);
      this.state.ui.message = `${resolvedRoomName(item)} has been duplicated.`;
      this.save(false);
      this.render();
    }

    removeWindow(id) {
      this.state.windows = this.state.windows.filter((x) => x.id !== id);
      this.save(false);
      this.render();
    }

    newWindow() {
      const fresh = createInitialState(this.track);
      this.state.draft = fresh.draft;
      this.state.activeSection = "track-layout";
      this.state.completedSections = [];
      this.state.editingWindowId = null;
      this.state.ui.windowSaved = false;
      this.state.ui.projectReview = false;
      this.state.ui.projectSaved = false;
      this.state.ui.message = "";
      this.save(false);
      this.render();
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    reviewProject() {
      if (!this.state.windows.length) {
        this.setUI({ error: "Save at least one window before reviewing the project." });
        return;
      }
      this.state.ui.windowSaved = false;
      this.state.ui.projectReview = true;
      this.state.ui.error = "";
      this.render();
      requestAnimationFrame(() => {
        this.root.querySelector(".lux-ct-project-review")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    backToBuilder() {
      this.state.ui.windowSaved = false;
      this.state.ui.projectReview = false;
      this.render();
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    saveProject() {
      this.save(false);
      this.state.ui.projectSaved = true;
      this.state.ui.message = "Project saved on this device.";
      this.render();
      requestAnimationFrame(() => {
        this.root.querySelector(".lux-ct-project-saved")
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }

    save(showMessage) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          version: APP_VERSION,
          activeTrackId: this.track.id,
          activeSection: this.state.activeSection,
          completedSections: this.state.completedSections,
          draft: this.state.draft,
          windows: this.state.windows
        }));
        if (showMessage) {
          this.state.ui.message = "Your project has been saved on this device.";
          this.render();
        }
      } catch (error) {
        console.warn("LUX Track Studio: could not save project", error);
      }
    }

    restore() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed.activeTrackId !== this.track.id) return;
        if (parsed.draft) this.state.draft = hydrateDraft(parsed.draft, this.track);
        if (Array.isArray(parsed.windows)) this.state.windows = parsed.windows.map((x) => hydrateDraft(x, this.track));
        this.state.activeSection = parsed.activeSection || "track-layout";
        this.state.completedSections = Array.isArray(parsed.completedSections) ? parsed.completedSections : [];
      } catch (error) {
        console.warn("LUX Track Studio: saved project could not be restored", error);
      }
    }

    clearProject() {
      localStorage.removeItem(STORAGE_KEY);
      this.state = createInitialState(this.track);
      this.state.ui.message = "The saved project has been cleared.";
      this.render();
    }

    setUI(patch) {
      Object.assign(this.state.ui, patch);
      this.render();
    }

    async checkout() {
      if (!this.state.windows.length) {
        const validation = validateDraft(this.track, this.state.draft);
        if (!validation.valid) {
          this.setUI({ error: "Complete and save the current window before checkout. " + validation.errors.join(" ") });
          return;
        }
        this.state.windows.push(clone(this.state.draft));
      }
      if (!this.settings.checkoutVariantId) {
        this.setUI({ error: "Checkout is not connected. Select the $0.01 Design Studio product in the theme editor." });
        return;
      }
      this.state.ui.busy = true;
      this.render();
      try {
        // Start from a clean cart so old test quantities or manually edited cart
        // values cannot carry into this Design Studio checkout.
        await fetch(this.settings.cartClearUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" }
        });

        const items = this.state.windows.map((windowItem) => {
          const manufacturing = buildManufacturingData(this.track, windowItem);
          return {
            id: Number(this.settings.checkoutVariantId),
            quantity: checkoutQuantity(manufacturing.price.total, this.settings.pricingMode),
            properties: buildShopifyProperties(manufacturing)
          };
        });
        const response = await fetch(this.settings.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const details = await safeJson(response);
          throw new Error(details?.description || details?.message || "Shopify rejected the cart request.");
        }
        location.assign(this.settings.checkoutUrl);
      } catch (error) {
        this.state.ui.busy = false;
        this.setUI({ error: error.message || "Unable to add this project to the cart." });
      }
    }

    render() {
      if (!this.track || !this.state) {
        this.root.innerHTML = renderTrackRequired();
        return;
      }
      const pricing = calculateTrackPrice(this.track, this.state.draft);
      this.root.innerHTML = `
        <div class="lux-ct-app lux-ct-app--v3" data-version="${escapeHtml(APP_VERSION)}">
          ${renderStatus(this.state.ui)}
          ${renderHero(this.track)}
          <div class="lux-ct-layout lux-ct-layout--accordion">
            <main class="lux-ct-main">
              ${this.state.ui.projectReview
                ? renderFullProjectReview(this.track, this.state.windows, this.currency, this.state.ui.busy)
                : this.state.ui.windowSaved
                  ? renderAfterSave(this.track, this.state.windows, this.currency)
                  : `<div class="lux-ct-accordion">
                      ${visibleSections(this.track).map((section) =>
                        renderAccordionSection(section, this.track, this.state, this.currency)
                      ).join("")}
                    </div>`
              }
            </main>
            <aside class="lux-ct-sidebar">
              <div data-live-summary>${renderLiveSummaryContent(this.track, this.state.draft, pricing, this.currency, this.productImage)}</div>
              ${renderProjectPanel(this.track, this.state.windows, this.currency, this.state.ui.busy, this.state.ui.projectSaved)}
            </aside>
          </div>
          ${renderMobileFooter(pricing, this.currency)}
          ${renderMobileSummarySheet(this.track, this.state.draft, pricing, this.currency, this.state.ui.summaryOpen, this.productImage)}
        </div>
      `;
    }
  }

  /* ==========================================================================
     9. ACCORDION VALIDATION AND SUMMARIES
     ========================================================================== */

  function sectionDef(id, title, description) { return { id, title, description }; }

  function visibleSections(track) {
    return ACCORDION_SECTIONS.filter((section) => {
      if (section.id === "track-layout" && track.manufacturing.hideTrackLayout) return false;

      if (section.id === "track-shape" && track.manufacturing.hideTrackShape) return false;
      if (section.id === "included-hardware" && track.manufacturing.hideHardware) return false;
      if (section.id === "extras" && !track.optionalExtras.length) return false;
      return true;
    });
  }

  function validateAccordionSection(id, track, draft) {
    const all = validateDraft(track, draft);
    const tokens = {
      "track-layout": [],
      "room-width": ["room", "length", "price tier", "minimum", "maximum"],
      "operation": ["operating style", "cord control"],
      "heading-stacking": ["pleating", "stacking", "runner quantity"],
      "fixing-colour": ["fixing", "colour"],
      "track-shape": ["track shape", "bay window", "curved track"],
      "extras": [],
      "included-hardware": [],
      "customer-notes": [],
      "review-save": null
    };
    if (tokens[id] === null) return all;
    const errors = all.errors.filter((error) =>
      (tokens[id] || []).some((token) => error.toLowerCase().includes(token))
    );
    return { valid: errors.length === 0, errors };
  }

  function accordionSummary(id, track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    const lines = draft.tracks.slice(0, count);
    const map = {
      "track-layout": count === 2 ? "Double Track" : "Single Track",
      "room-width": `${resolvedRoomName(draft) || "Room not selected"} · ${draft.lengthMm || 0} mm`,
      "operation": lines.map((line) => labelFor(track.operations, line.operation)).join(" · "),
      "heading-stacking": lines.map((line) => track.manufacturing.hideHeading
        ? labelFor(track.stacking, line.stacking)
        : `${labelFor(track.pleating, line.pleating)} · ${labelFor(track.stacking, line.stacking)}`
      ).join(" | "),
      "fixing-colour": `${labelFor(track.fixing, draft.fixing)} · ${labelFor(track.colours, draft.colour)}`,
      "track-shape": track.bending.length ? labelFor(track.bending, draft.bending) : "Not applicable",
      "extras": draft.optionalExtras.length
        ? draft.optionalExtras.map((value) => labelFor(track.optionalExtras, value)).join(", ")
        : "No optional extras",
      "included-hardware": hardwareSummary(track, draft),
      "customer-notes": String(draft.notes || "").trim() || "No notes added",
      "review-save": "Review the complete window specification"
    };
    return map[id] || "";
  }

  function hardwareSummary(track, draft) {
    if (track.manufacturing.hideHardware) return "Not applicable";
    const count = draft.trackCount === 2 ? 2 : 1;
    const brackets = getBracketCount(draft.lengthMm);
    const bracketType = count === 2 ? "double brackets" : "single brackets";
    const runners = draft.tracks.slice(0, count)
      .reduce((sumValue, line) => sumValue + getRunnerAllocation(line, draft.lengthMm).totalCarriers, 0);
    return `${brackets} ${bracketType} · ${runners} runners`;
  }

  /* ==========================================================================
     10. TRACK STUDIO 3.0 RENDER FUNCTIONS
     ========================================================================== */

  function renderHero(track) {
    return `
      <header class="lux-ct-hero">
        <div class="lux-ct-hero-inner">
          <p class="lux-ct-powered">Powered By</p>
          <p class="lux-ct-brand">LUXWORLD DESIGN STUDIO™</p>
          <h1>Design Your Custom Curtain Track</h1>
          <div class="lux-ct-track-pill">Selected Track <strong>${escapeHtml(track.shortName)}</strong></div>
          <p class="lux-ct-track-title">${escapeHtml(getStudioDisplayName(track))}</p>
        </div>
      </header>
    `;
  }

  function renderAccordionSection(section, track, state, currency) {
    const active = state.activeSection === section.id;
    const complete = state.completedSections.includes(section.id);
    const sectionTitle = section.id === "heading-stacking" && track.manufacturing.hideHeading ? "Stacking" : section.title;
    const sectionDescription = section.id === "heading-stacking" && track.manufacturing.hideHeading
      ? "Choose the blind opening direction."
      : section.description;
    return `
      <section class="lux-ct-accordion-section ${active ? "is-open" : ""} ${complete ? "is-complete" : ""}"
        data-accordion-section="${escapeAttr(section.id)}">
        <button type="button" class="lux-ct-accordion-trigger" data-action="open-section"
          data-section="${escapeAttr(section.id)}" aria-expanded="${active}">
          <span class="lux-ct-accordion-status">${complete ? "◆" : active ? "▼" : "◇"}</span>
          <span class="lux-ct-accordion-heading">
            <strong>${escapeHtml(sectionTitle)}</strong>
            <small>${escapeHtml(active ? sectionDescription : accordionSummary(section.id, track, state.draft))}</small>
          </span>
          <span class="lux-ct-accordion-edit">${active ? "Open" : complete ? "Edit" : "Select"}</span>
        </button>
        ${active ? `
          <div class="lux-ct-accordion-panel">
            ${renderAccordionContent(section.id, track, state.draft, currency)}
            ${section.id !== "review-save" ? `
              <div class="lux-ct-accordion-actions">
                <button type="button" class="lux-ct-primary" data-action="complete-section"
                  data-section="${escapeAttr(section.id)}">Save & Continue</button>
              </div>` : ""}
          </div>` : ""}
      </section>
    `;
  }

  function renderAccordionContent(id, track, draft, currency) {
    switch (id) {
      case "track-layout": return renderTrackCount(track, draft);
      case "room-width": return renderRoomLength(track, draft, currency);
      case "operation": return renderOperations(track, draft);
      case "heading-stacking": return renderHeadingStacking(track, draft);
      case "fixing-colour": return renderFixingColour(track, draft);
      case "track-shape": return renderBending(track, draft);
      case "extras": return renderExtras(track, draft, currency);
      case "included-hardware": return renderIncludedHardware(track, draft);
      case "customer-notes": return renderNotes(draft);
      case "review-save": return renderReview(track, draft, currency);
      default: return "";
    }
  }

  function renderTrackCount(track, draft) {
    const options = [
      { value: "1", label: "Single Track", description: "One curtain track." },
      ...(track.manufacturing.supportsDoubleTrack
        ? [{ value: "2", label: "Double Track", description: "Front and back tracks configured independently." }]
        : [])
    ];
    return renderCards(options, String(draft.trackCount), "trackCount");
  }

  function renderRoomLength(track, draft, currency) {
    const tier = getBaseTrackPrice(track, draft.lengthMm);
    const rooms = ["Living Room","Dining Room","Master Bedroom","Bedroom","Theatre Room","Office","Custom"];
    return `
      <div class="lux-ct-field-grid">
        <label class="lux-ct-field"><span>Room name</span>
          <select data-field="roomName">${rooms.map((room) =>
            `<option value="${escapeAttr(room)}" ${draft.roomName === room ? "selected" : ""}>${escapeHtml(room)}</option>`
          ).join("")}</select>
        </label>
        ${draft.roomName === "Custom" ? `
          <label class="lux-ct-field"><span>Custom room name</span>
            <input type="text" value="${escapeAttr(draft.customRoomName)}" data-field="customRoomName"
              placeholder="Example: Front lounge">
          </label>` : ""}
        <label class="lux-ct-field"><span>Exact track width (mm)</span>
          <input type="number" min="${track.length.minimum}" max="${track.length.maximum}" step="1"
            value="${escapeAttr(draft.lengthMm)}" data-field="lengthMm" inputmode="numeric">
          <small>Priced to the next available tier. Minimum ${track.length.minimum} mm.</small>
        </label>
      </div>
      <div class="lux-ct-info-panel"><strong>Current price tier</strong>
        <span>${tier?.tier ? `${tier.tier} mm — ${currency.format(tier.price)} per track` : "No price tier available"}</span>
      </div>
    `;
  }

  function renderOperations(track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    return `<div class="lux-ct-track-lines">${draft.tracks.slice(0, count).map((line, index) => {
      const config = findByValue(track.operations, line.operation);
      return `<article class="lux-ct-track-line">
        <div class="lux-ct-track-line-head"><h3>${escapeHtml(line.label)}</h3><span>${index ? "Back" : "Front"}</span></div>
        ${renderCards(track.operations, line.operation, "operation", index)}
        ${config?.requiresSideSelection ? `<h4 class="lux-ct-choice-heading">${line.operation === "chain" ? "Chain" : "Cord"} control side</h4>${renderCards(config.sideOptions, line.cordSide, "cordSide", index)}` : ""}
      </article>`;
    }).join("")}</div>`;
  }

  function renderHeadingStacking(track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    return `<div class="lux-ct-track-lines">${draft.tracks.slice(0, count).map((line, index) => {
      const allocation = getRunnerAllocation(line, draft.lengthMm);
      return `<article class="lux-ct-track-line">
        <div class="lux-ct-track-line-head"><h3>${escapeHtml(line.label)}</h3><span>${index ? "Back" : "Front"}</span></div>
        ${track.manufacturing.hideHeading ? "" : `<h4 class="lux-ct-choice-heading">Heading style</h4>${renderCards(track.pleating, line.pleating, "pleating", index)}`}
        <h4 class="lux-ct-choice-heading">Stacking</h4>${renderCards(track.stacking, line.stacking, "stacking", index)}
        ${track.manufacturing.requiresRunnerSelection ? `<h4 class="lux-ct-choice-heading">Runner selection</h4>${renderCards([
          { value:"standard", label:"Standard Runner Count", description:"Automatically calculated." },
          { value:"custom", label:"Specify Runner Quantity", description:"Use only for a special quantity." }
        ], line.runnerMode, "runnerMode", index)}
        ${line.runnerMode === "custom" ? `<label class="lux-ct-field"><span>Custom runner quantity</span>
          <input type="number" min="1" step="1" value="${escapeAttr(line.customRunnerQuantity)}"
            data-field="customRunnerQuantity" data-track-index="${index}"></label>` : ""}
        <div class="lux-ct-info-panel"><strong>Runners included</strong>
          <span>${renderCarrierText(allocation, line.stacking)}</span></div>` : ""}
      </article>`;
    }).join("")}</div>`;
  }

  function renderFixingColour(track, draft) {
    return `<h3>Fixing method</h3>${renderCards(track.fixing, draft.fixing, "fixing")}
      <h3>Track colour</h3>${renderCards(track.colours, draft.colour, "colour")}`;
  }

  function renderBending(track, draft) {
    return `${renderCards(track.bending, draft.bending, "bending")}
      ${draft.bending === "bay" ? `<div class="lux-ct-field-grid lux-ct-dimensions">
        ${dimensionInput("A","bendDimensions.a",draft.bendDimensions.a)}
        ${dimensionInput("B","bendDimensions.b",draft.bendDimensions.b)}
        ${dimensionInput("C","bendDimensions.c",draft.bendDimensions.c)}</div>
        <p class="lux-ct-help">A + B + C must equal the total track length.</p>` : ""}
      ${draft.bending === "curved" ? `<div class="lux-ct-field-grid lux-ct-dimensions">
        ${dimensionInput("A","bendDimensions.a",draft.bendDimensions.a)}
        ${dimensionInput("B","bendDimensions.b",draft.bendDimensions.b)}</div>
        <p class="lux-ct-help">Provide the bending measurements in millimetres (mm).</p>` : ""}`;
  }

  function renderExtras(track, draft, currency) {
    return `<div class="lux-ct-option-grid">${track.optionalExtras.map((item) => {
      const selected = draft.optionalExtras.includes(item.value);
      return `<button type="button" class="lux-ct-option ${selected ? "is-selected" : ""}"
        data-action="toggle-extra" data-value="${escapeAttr(item.value)}">
        <span class="lux-ct-option-check">${selected ? "◆" : "◇"}</span>
        <strong>${escapeHtml(item.label)}</strong>
        <small>${item.price ? `Add ${currency.format(item.price)}` : "Included"}</small></button>`;
    }).join("")}</div>`;
  }

  function renderIncludedHardware(track, draft) {
    const count = draft.trackCount === 2 ? 2 : 1;
    const bracketCount = getBracketCount(draft.lengthMm);
    const bracketType = count === 2 ? "Double track brackets" : "Single track brackets";
    return `<div class="lux-ct-hardware-grid">
      <article class="lux-ct-hardware-card"><span>${bracketType}</span>
        <strong>${bracketCount}</strong><small>Quantity supplied for this track length</small></article>
      ${draft.tracks.slice(0, count).map((line) => {
        const allocation = getRunnerAllocation(line, draft.lengthMm);
        return `<article class="lux-ct-hardware-card"><span>${escapeHtml(line.label)} runners</span>
          <strong>${allocation.totalCarriers}</strong>
          <small>${escapeHtml(renderCarrierText(allocation, line.stacking))}</small></article>`;
      }).join("")}
    </div>`;
  }

  function renderNotes(draft) {
    return `<label class="lux-ct-field"><span>Customer notes</span>
      <textarea rows="7" data-field="notes" placeholder="Add important manufacturing or installation information.">${escapeHtml(draft.notes)}</textarea></label>`;
  }

  function renderReview(track, draft, currency) {
    const data = buildManufacturingData(track, draft);
    const validation = validateDraft(track, draft);
    return `${!validation.valid ? `<div class="lux-ct-review-warning"><strong>Please correct:</strong> ${escapeHtml(validation.errors.join(" "))}</div>` : ""}
      <div class="lux-ct-review-grid">
        ${reviewRow("Room", data.roomName)}${reviewRow("Track", track.shortName)}
        ${reviewRow("Configuration", data.trackCount)}${reviewRow("Finished width", `${data.enteredLengthMm} mm`)}
        ${reviewRow("Fixing", data.fixing)}${reviewRow("Colour", data.colour)}
        ${data.bending ? reviewRow("Shape", data.bending) : ""}
        ${data.bracketQuantity !== undefined ? reviewRow("Bracket type", data.bracketType) + reviewRow("Bracket quantity", String(data.bracketQuantity)) : ""}
      </div>
      <div class="lux-ct-review-tracks">${data.tracks.map((line) => `<article><h3>${escapeHtml(line.position)} Track</h3>
        ${reviewRow("Operation", line.operation)}${line.controlSide ? reviewRow("Control side", line.controlSide) : ""}
        ${track.manufacturing.hideHeading ? "" : reviewRow("Heading", line.pleating)}${reviewRow("Stacking", line.stacking)}
        ${track.manufacturing.hideHardware ? "" : reviewRow("Total runners", String(line.runnersTotal))}</article>`).join("")}</div>
      <div class="lux-ct-review-total"><span>Configured price</span><strong>${currency.format(data.price.total)}</strong></div>
      <button type="button" class="lux-ct-primary lux-ct-save-window" data-action="save-window">Save This Window</button>`;
  }

  function renderLiveSummaryContent(track, draft, pricing, currency, productImage = "") {
    const count = draft.trackCount === 2 ? 2 : 1;
    return `<section class="lux-ct-summary-card">
      <div class="lux-ct-summary-product-image"${productImage ? "" : " hidden"}>
        <img
          data-track-hero-image
          src="${escapeAttr(productImage || "")}"
          alt="${escapeAttr(track.heroTitle || track.shortName)}"
          loading="eager"
          decoding="async"
          ${productImage ? "" : "hidden"}
          onerror="this.hidden=true;this.parentElement.hidden=true"
        >
      </div>
      <div class="lux-ct-summary-head"><p>${escapeHtml(track.shortName)}</p><h2>${escapeHtml(resolvedRoomName(draft) || "Current Window")}</h2></div>
      <div class="lux-ct-summary-list">
        ${summaryRow("Width", `${draft.lengthMm || 0} mm`)}
        ${summaryRow("System", count === 2 ? "Double Track" : "Single Track")}
        ${summaryRow("Fixing", labelFor(track.fixing, draft.fixing))}
        ${summaryRow("Colour", labelFor(track.colours, draft.colour))}
        ${track.bending.length ? summaryRow("Shape", labelFor(track.bending, draft.bending)) : ""}
        ${track.manufacturing.hideHardware ? "" : summaryRow("Brackets", `${getBracketCount(draft.lengthMm)} ${count === 2 ? "double track" : "single track"} brackets included`)}
      </div>
      <div class="lux-ct-summary-lines">${draft.tracks.slice(0, count).map((line) => {
        const allocation = getRunnerAllocation(line, draft.lengthMm);
        return `<div><strong>${escapeHtml(line.label)}</strong>
          <span>${escapeHtml(labelFor(track.operations, line.operation))}</span>
          ${track.manufacturing.hideHeading ? "" : `<span>${escapeHtml(labelFor(track.pleating, line.pleating))}</span>`}
          <span>${escapeHtml(labelFor(track.stacking, line.stacking))}</span>
          ${track.manufacturing.hideHardware ? "" : `<span>${escapeHtml(renderCarrierText(allocation, line.stacking))}</span>`}</div>`;
      }).join("")}</div>
      <div class="lux-ct-price-breakdown">
        ${summaryRow("Base track", currency.format(pricing.baseTotal))}
        ${pricing.operationTotal ? summaryRow("Operation upgrades", currency.format(pricing.operationTotal)) : ""}
        ${pricing.pleatingTotal ? summaryRow("S-Wave heading", currency.format(pricing.pleatingTotal)) : ""}
        ${pricing.bendingTotal ? summaryRow("Track shape", currency.format(pricing.bendingTotal)) : ""}
        ${pricing.extrasTotal ? summaryRow("Optional extras", currency.format(pricing.extrasTotal)) : ""}
      </div>
      <div class="lux-ct-summary-total"><span>Current total</span><strong>${currency.format(pricing.total)}</strong></div>
    </section>`;
  }

  function renderProjectPanel(track, windows, currency, busy, projectSaved = false) {
    const total = windows.reduce((sumValue, item) => sumValue + calculateTrackPrice(track, item).total, 0);
    return `<section class="lux-ct-project-panel">
      <div class="lux-ct-saved-head"><div><p>Your Project</p><h3>${windows.length} saved window${windows.length === 1 ? "" : "s"}</h3></div>
        <button type="button" class="lux-ct-text-button" data-action="new-window">+ Add window</button></div>
      ${windows.length ? `<div class="lux-ct-saved-list">${windows.map((item) => {
        const price = calculateTrackPrice(track, item);
        const line = item.tracks[0];
        return `<article class="lux-ct-saved-item"><div><strong>${escapeHtml(resolvedRoomName(item))}</strong>
          <span>${item.lengthMm} mm · ${item.trackCount === 2 ? "Double Track" : "Single Track"}</span>
          <span>${escapeHtml(labelFor(track.pleating, line.pleating))} · ${escapeHtml(labelFor(track.colours, item.colour))} · ${escapeHtml(labelFor(track.operations, line.operation))}</span>
          <b>${currency.format(price.total)}</b></div>
          <div class="lux-ct-saved-actions">
            <button type="button" data-action="edit-window" data-id="${escapeAttr(item.id)}">Edit</button>
            <button type="button" data-action="duplicate-window" data-id="${escapeAttr(item.id)}">Duplicate</button>
            <button type="button" data-action="remove-window" data-id="${escapeAttr(item.id)}">Remove</button>
          </div></article>`;
      }).join("")}</div>
      <div class="lux-ct-project-total"><span>Project total</span><strong>${currency.format(total)}</strong></div>
      <div class="lux-ct-project-actions">
        <button type="button" class="lux-ct-secondary" data-action="review-project">Review Project</button>
        <button type="button" class="lux-ct-primary" data-action="checkout" ${busy ? "disabled" : ""}>${busy ? "Preparing Checkout…" : "Checkout"}</button>
        <button type="button" class="lux-ct-text-button" data-action="save-project">${projectSaved ? "Project Saved ✓" : "Save Project"}</button>
        ${projectSaved ? `<p class="lux-ct-project-saved">Saved securely on this device.</p>` : ""}
        <button type="button" class="lux-ct-text-button lux-ct-danger" data-action="clear-project">Clear Project</button>
      </div>` : `<p class="lux-ct-empty">Complete the accordion and save your first window.</p>`}
    </section>`;
  }

  function renderAfterSave(track, windows, currency) {
    const last = windows.at(-1);
    const total = windows.reduce((sumValue, item) => sumValue + calculateTrackPrice(track, item).total, 0);

    return `<section class="lux-ct-after-save">
      <div class="lux-ct-success-mark">◆</div>
      <p class="lux-ct-eyebrow">Window Saved</p>
      <h2>${escapeHtml(resolvedRoomName(last))} has been added</h2>
      <p>Your project now contains ${windows.length} window${windows.length === 1 ? "" : "s"}.</p>
      <div class="lux-ct-after-save-total">
        <span>Current project total</span>
        <strong>${currency.format(total)}</strong>
      </div>
      <div class="lux-ct-after-save-actions">
        <button type="button" class="lux-ct-primary" data-action="new-window">Add Another Window</button>
        <button type="button" class="lux-ct-secondary" data-action="review-project">Review Project</button>
      </div>
    </section>`;
  }

  function renderFullProjectReview(track, windows, currency, busy) {
    const total = windows.reduce((sumValue, item) => sumValue + calculateTrackPrice(track, item).total, 0);

    return `<section class="lux-ct-project-review">
      <div class="lux-ct-project-review-head">
        <div>
          <p class="lux-ct-eyebrow">Project Review</p>
          <h2>${windows.length} window${windows.length === 1 ? "" : "s"}</h2>
        </div>
        <strong>${currency.format(total)}</strong>
      </div>

      <div class="lux-ct-project-review-list">
        ${windows.map((item, index) => {
          const data = buildManufacturingData(track, item);
          return `<article>
            <div class="lux-ct-project-review-number">${index + 1}</div>
            <div>
              <h3>${escapeHtml(data.roomName)}</h3>
              <p>${escapeHtml(track.shortName)} · ${data.enteredLengthMm} mm · ${escapeHtml(data.trackCount)}</p>
              <p>${escapeHtml(data.tracks[0].operation)} · ${escapeHtml(data.tracks[0].pleating)} · ${escapeHtml(data.colour)}</p>
            </div>
            <strong>${currency.format(data.price.total)}</strong>
          </article>`;
        }).join("")}
      </div>

      <div class="lux-ct-after-save-actions">
        <button type="button" class="lux-ct-secondary" data-action="back-to-builder">Back to Builder</button>
        <button type="button" class="lux-ct-primary" data-action="checkout" ${busy ? "disabled" : ""}>
          ${busy ? "Preparing Checkout…" : "Proceed to Checkout"}
        </button>
      </div>
    </section>`;
  }

  function renderMobileFooter(pricing, currency) {
    return `<div class="lux-ct-mobile-footer"><div><span>Current total</span>
      <strong data-mobile-total>${currency.format(pricing.total)}</strong></div>
      <button type="button" class="lux-ct-primary" data-action="open-summary">Review</button></div>`;
  }

  function renderMobileSummarySheet(track, draft, pricing, currency, open, productImage = "") {
    return `<div class="lux-ct-sheet ${open ? "is-open" : ""}" aria-hidden="${open ? "false" : "true"}">
      <button type="button" class="lux-ct-sheet-backdrop" data-action="close-summary" aria-label="Close summary"></button>
      <div class="lux-ct-sheet-panel"><div class="lux-ct-sheet-head"><div><span>Current configuration</span>
        <strong>${escapeHtml(track.shortName)}</strong></div>
        <button type="button" data-action="close-summary" aria-label="Close">×</button></div>
        <div data-live-summary>${renderLiveSummaryContent(track, draft, pricing, currency, productImage)}</div></div></div>`;
  }

  function renderStatus(ui) {
    if (!ui.message && !ui.error) return "";
    return `<div class="lux-ct-status ${ui.error ? "is-error" : "is-success"}" role="status">
      <span>${escapeHtml(ui.error || ui.message)}</span>
      <button type="button" data-action="close-message" aria-label="Close">×</button></div>`;
  }

  function renderCards(items, selectedValue, field, trackIndex) {
    const colourCards = field === "colour";

    return `<div class="lux-ct-option-grid ${colourCards ? "lux-ct-option-grid--colour" : ""}">${items.map((item) => {
      const selected = String(item.value) === String(selectedValue);
      const colour = colourCards
        ? `<span class="lux-ct-colour-swatch lux-ct-colour-swatch--${escapeAttr(item.value)}" aria-hidden="true"></span>`
        : "";

      return `<button type="button"
        class="lux-ct-option ${colourCards ? "lux-ct-option--colour" : ""} ${selected ? "is-selected" : ""}"
        data-action="select-option"
        data-field="${escapeAttr(field)}"
        data-value="${escapeAttr(item.value)}"
        ${trackIndex !== undefined ? `data-track-index="${trackIndex}"` : ""}
        aria-pressed="${selected}">
        <span class="lux-ct-option-check">${selected ? "◆" : "◇"}</span>
        ${colour}
        <span class="lux-ct-option-copy">
          <strong>${escapeHtml(item.label)}</strong>
          ${item.description ? `<small>${escapeHtml(item.description)}</small>` : ""}
          ${item.price ? `<em>+${formatPlainCurrency(item.price)}</em>` : ""}
        </span>
      </button>`;
    }).join("")}</div>`;
  }

  function renderTrackRequired() {
    return `<div class="lux-ct-app lux-ct-no-track"><header class="lux-ct-hero"><div class="lux-ct-hero-inner">
      <p class="lux-ct-powered">Powered By</p><p class="lux-ct-brand">LUXWORLD DESIGN STUDIO™</p>
      <h1>Select Your Curtain Track</h1><p class="lux-ct-track-title">Return to the curtain-track collection and select a track.</p>
      <a class="lux-ct-primary lux-ct-return-link" href="/collections/collections-curtain-tracks-perth">Choose Your Curtain Track</a>
    </div></header></div>`;
  }

  function getStudioDisplayName(track) {
    return String(track.heroTitle || track.displayName || track.shortName || "Curtain Track")
      .replace(/\bPerth\b/gi, "").replace(/\s+\|\s*$/g, "").replace(/\s{2,}/g, " ").trim();
  }

  function cssEscape(value) {
    return window.CSS?.escape ? CSS.escape(String(value)) : String(value).replace(/["\\]/g, "\\$&");
  }

  /* ==========================================================================
     11. SHOPIFY ADAPTER
     ========================================================================== */

  function buildShopifyProperties(manufacturing) {
    const properties = {
      "_Luxworld Design Studio": "Curtain Track Design Studio",
      "_Luxworld Studio Type": "Curtain Track",
      "_Luxworld Studio Version": APP_VERSION,
      "_LUX Studio Version": APP_VERSION,
      "_Luxworld Design Studio Item": "true",
      "Track Collection": manufacturing.collection,
      "Room": manufacturing.roomName,
      "Track Configuration": manufacturing.trackCount,
      "Entered Length": `${manufacturing.enteredLengthMm} mm`,
      "Charged Length Tier": `${manufacturing.chargedLengthTierMm} mm`,
      "Fixing": manufacturing.fixing,
      "Colour": manufacturing.colour,
      "Configured Price": formatPlainCurrency(manufacturing.price.total),
      "_Configuration JSON": JSON.stringify(manufacturing)
    };


    if (manufacturing.bending) properties["Bending"] = manufacturing.bending;
    if (manufacturing.bracketQuantity !== undefined) {
      properties["Bracket Type"] = manufacturing.bracketType;
      properties["Bracket Quantity"] = String(manufacturing.bracketQuantity);
    }
    manufacturing.tracks.forEach((trackLine) => {
      const prefix = `${trackLine.position} Track`;
      properties[`${prefix} Operation`] = trackLine.operation;
      if (trackLine.controlSide) properties[`${prefix} Control Side`] = trackLine.controlSide;
      if (trackLine.pleating) properties[`${prefix} Pleating`] = trackLine.pleating;
      properties[`${prefix} Stacking`] = trackLine.stacking;
      if (trackLine.runnersTotal !== undefined) {
        if (trackLine.runnersPerSide !== undefined) {
          properties[`${prefix} Runners Per Side`] = String(trackLine.runnersPerSide);
        }
        properties[`${prefix} Total Runners`] = String(trackLine.runnersTotal);
      }
    });

    if (manufacturing.optionalExtras?.length) {
      properties["Optional Extras"] = manufacturing.optionalExtras.join(", ");
    }
    if (manufacturing.notes) properties["Customer Notes"] = manufacturing.notes;
    return properties;
  }

  function checkoutQuantity(total, pricingMode) {
    if (pricingMode === "cents-quantity") return Math.max(1, Math.round(total * 100));
    if (pricingMode === "dollar-quantity") return Math.max(1, Math.round(total));
    return 1;
  }

  /* ==========================================================================
     12. TRACK RESOLUTION, HYDRATION AND SETTINGS
     ========================================================================== */

  function resolveProductImageFromLocation(root) {
    const params = new URLSearchParams(window.location.search);
    const candidates = [
      params.get("image"),
      params.get("hero_image"),
      params.get("featured_image"),
      root?.dataset?.productImage,
      root?.dataset?.collectionImage,
      root?.dataset?.heroImage
    ];

    for (const candidate of candidates) {
      const normalised = normaliseImageUrl(candidate);
      if (normalised) return normalised;
    }

    return "";
  }

  async function resolveTrackHeroImage(track, root, currentImage = "") {
    const directImage = normaliseImageUrl(currentImage) || resolveProductImageFromLocation(root);
    if (directImage) return directImage;

    const params = new URLSearchParams(window.location.search);
    const productHandle = normaliseHandle(
      params.get("product") ||
      track.productHandle ||
      ""
    );

    if (!productHandle) return "";

    const endpoints = [
      `/products/${encodeURIComponent(productHandle)}.js`,
      `/products/${encodeURIComponent(productHandle)}.json`
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: { "Accept": "application/json" },
          credentials: "same-origin"
        });

        if (!response.ok) continue;

        const payload = await response.json();
        const product = payload?.product || payload;
        const candidates = [
          product?.featured_image,
          product?.featured_media?.preview_image?.src,
          product?.image?.src,
          Array.isArray(product?.images) ? (product.images[0]?.src || product.images[0]) : ""
        ];

        for (const candidate of candidates) {
          const image = normaliseImageUrl(candidate);
          if (image) return image;
        }
      } catch (error) {
        console.warn(`LUX Track Studio: could not load image from ${endpoint}`, error);
      }
    }

    return "";
  }

  function normaliseImageUrl(value) {
    if (!value) return "";

    const raw = typeof value === "object"
      ? value.src || value.url || value.originalSrc || ""
      : String(value);

    if (!raw.trim()) return "";

    try {
      const protocolSafe = raw.trim().startsWith("//")
        ? `${window.location.protocol}${raw.trim()}`
        : raw.trim();

      const url = new URL(protocolSafe, window.location.origin);
      if (!["http:", "https:"].includes(url.protocol)) return "";
      return url.href;
    } catch (_) {
      return "";
    }
  }


  function resolveTrackFromLocation() {
    const params = new URLSearchParams(window.location.search);
    const rawRequested = params.get("track") || params.get("collection") || "";
    const requested = normaliseHandle(rawRequested);
    if (!requested) return null;

    if (LUX_TRACK_DATABASE[requested]) return LUX_TRACK_DATABASE[requested];

    return Object.values(LUX_TRACK_DATABASE).find((track) => {
      return (track.aliases || []).map(normaliseHandle).includes(requested) ||
        normaliseHandle(track.collectionHandle) === requested ||
        normaliseHandle(track.productHandle) === requested;
    }) || null;
  }

  function hydrateDraft(input, track) {
    const base = createInitialState(track).draft;
    const result = deepMerge(base, input || {});
    result.trackCount = result.trackCount === 2 ? 2 : 1;
    result.lengthMm = Number(result.lengthMm) || track.length.minimum;
    result.tracks = [0, 1].map((index) => deepMerge(base.tracks[index], result.tracks?.[index] || {}));
    result.optionalExtras = Array.isArray(result.optionalExtras) ? result.optionalExtras : [];
    return result;
  }

  function readSettings(root) {
    return {
      currency: root.dataset.currency || "AUD",
      locale: root.dataset.locale || "en-AU",
      cartAddUrl: root.dataset.cartAddUrl || "/cart/add.js",
      cartUrl: root.dataset.cartUrl || "/cart",
      cartClearUrl: root.dataset.cartClearUrl || "/cart/clear.js",
      checkoutUrl: root.dataset.checkoutUrl || "/checkout",
      checkoutVariantId: root.dataset.checkoutVariantId || "",
      pricingMode: root.dataset.pricingMode || "cents-quantity"
    };
  }

  /* ==========================================================================
     13. UTILITIES
     ========================================================================== */

  function option(value, label, extra = {}) {
    return { value, label, ...extra };
  }

  function pricedOption(value, label, price, extra = {}) {
    return option(value, label, { price, ...extra });
  }

  function operation(value, label, price, extra = {}) {
    return pricedOption(value, label, price, extra);
  }

  function priceTable(values) {
    return Object.freeze({ ...values });
  }

  function hardwareRow(maxLengthMm, brackets) {
    return { maxLengthMm, brackets };
  }

  function carrierRow(maxLengthMm, carriers) {
    return { maxLengthMm, carriers };
  }

  function centreCarrierRow(maxLengthMm, carriersPerSide) {
    return { maxLengthMm, carriersPerSide };
  }

  function step(id, title, description) {
    return { id, title, description };
  }

  function mergeTrack(defaults, overrides) {
    return deepMerge(clone(defaults), overrides);
  }

  function deepMerge(target, source) {
    if (!isPlainObject(target) || !isPlainObject(source)) return clone(source);
    const output = { ...target };
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = output[key];
      if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
        output[key] = deepMerge(targetValue, sourceValue);
      } else {
        output[key] = clone(sourceValue);
      }
    });
    return output;
  }

  function deepFreeze(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
    Object.freeze(value);
    Object.values(value).forEach(deepFreeze);
    return value;
  }

  function clone(value) {
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  }

  function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  }

  function findByValue(items, value) {
    return (items || []).find((item) => String(item.value) === String(value));
  }

  function labelFor(items, value) {
    return findByValue(items, value)?.label || String(value || "Not selected");
  }

  function getCeilingHardwareTier(lengthMm) {
    const width = toPositiveNumber(lengthMm);
    const tiers = LUX_BRACKET_TABLE.map((item) => item.maxLengthMm);
    return tiers.find((tier) => width <= tier) || tiers.at(-1);
  }

  function emptyCarrierAllocation(stacking) {
    return {
      mode: stacking === "centre-open" ? "centre-open" : "one-way",
      carriersPerSide: stacking === "centre-open" ? 0 : null,
      totalCarriers: 0
    };
  }

  function renderCarrierText(allocation, stacking) {
    if (stacking === "centre-open" && allocation.carriersPerSide !== null) {
      return `${allocation.carriersPerSide} per side · ${allocation.totalCarriers} total runners included`;
    }
    return `${allocation.totalCarriers} runners included`;
  }

  function resolvedRoomName(draft) {
    if (draft.roomName === "Custom") return String(draft.customRoomName || "").trim();
    return String(draft.roomName || "").trim();
  }

  function cleanDimensions(dimensions, keys) {
    return keys.reduce((result, key) => {
      const value = Number(dimensions[key]);
      if (value > 0) result[key.toUpperCase()] = value;
      return result;
    }, {});
  }

  function setByPath(target, path, value) {
    const parts = String(path).split(".");
    let current = target;
    parts.forEach((part, index) => {
      if (index === parts.length - 1) current[part] = value;
      else {
        if (!isPlainObject(current[part])) current[part] = {};
        current = current[part];
      }
    });
  }

  function normaliseFieldValue(path, value) {
    if (["trackCount", "lengthMm", "customRunnerQuantity", "bendDimensions.a", "bendDimensions.b", "bendDimensions.c"].includes(path)) {
      return value === "" ? "" : Number(value);
    }
    return value;
  }

  function toPositiveNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function sum(values) {
    return values.reduce((total, value) => total + (Number(value) || 0), 0);
  }

  function roundCurrency(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function titleCase(value) {
    return String(value || "").replace(/(^|[-_\s])\w/g, (match) => match.toUpperCase());
  }

  function normaliseHandle(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[™®]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function createId(prefix) {
    const random = Math.random().toString(36).slice(2, 9);
    return `${prefix}-${Date.now().toString(36)}-${random}`;
  }

  function formatPlainCurrency(value) {
    return `$${roundCurrency(value).toFixed(2)}`;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, "&#096;");
  }

  function dimensionInput(label, field, value) {
    return `
      <label class="lux-ct-field">
        <span>${label} measurement (mm)</span>
        <input type="number" min="1" step="1" value="${escapeAttr(value)}" data-field="${escapeAttr(field)}" inputmode="numeric">
      </label>
    `;
  }

  function reviewRow(label, value) {
    return `<div class="lux-ct-review-row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
  }

  function summaryRow(label, value) {
    return `<div class="lux-ct-summary-row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
  }

  async function safeJson(response) {
    try { return await response.json(); }
    catch (_) { return null; }
  }

  /* ==========================================================================
     14. PUBLIC DEBUG API AND INITIALISATION
     ========================================================================== */

  window.LuxworldCurtainTrackStudio = Object.freeze({
    version: APP_VERSION,
    database: LUX_TRACK_DATABASE,
    calculateTrackPrice,
    getBracketCount,
    getSWaveCarrierAllocation,
    buildManufacturingData,
    validateDraft
  });

  function builderUrl(trackId) {
    const resolved = normaliseHandle(trackId);
    return `/pages/curtain-track-design-studio?track=${encodeURIComponent(resolved)}`;
  }

  // Read-only public utilities for product-page CTA integration and testing.
  // The application itself remains encapsulated and does not rely on globals.
  window.LUXWORLD_CURTAIN_TRACK_STUDIO = Object.freeze({
    version: APP_VERSION,
    trackIds: Object.freeze(Object.keys(LUX_TRACK_DATABASE)),
    builderUrl,
    getTrack: (trackId) => {
      const key = normaliseHandle(trackId);
      return LUX_TRACK_DATABASE[key] || null;
    },
    calculate: (trackId, draft) => {
      const key = normaliseHandle(trackId);
      const track = LUX_TRACK_DATABASE[key];
      if (!track) return null;
      return calculateTrackPrice(track, hydrateDraft(draft || {}, track));
    }
  });

  function initialise() {
    document.querySelectorAll("[data-lux-curtain-track-studio]").forEach((root) => {
      if (root.dataset.initialised === "true") return;
      root.dataset.initialised = "true";
      try {
        new LuxCurtainTrackStudio(root);
      } catch (error) {
        console.error("LUX Curtain Track Studio failed to initialise", error);
        root.innerHTML = `
          <div class="lux-ct-fatal-error">
            <strong>The Curtain Track Design Studio could not load.</strong>
            <span>${escapeHtml(error.message || "Unknown error")}</span>
          </div>
        `;
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialise, { once: true });
  } else {
    initialise();
  }
})();
